<template>
  <div class="chart-container">

    <!-- 表格 -->
    <div v-if="String(type) === '1'" class="table-wrapper">
      <table class="data-table">
        <thead>
        <tr>
          <th v-for="(head, idx) in headers" :key="idx">{{ head }}</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(row, ridx) in chartData" :key="ridx">
          <td v-for="(cell, cidx) in row" :key="cidx">
            {{ cell === null || cell === '' ? '-' : cell }}
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- ECharts（v-show，DOM 始终存在，SSE 必须） -->
    <div
        v-show="['2', '3', '4'].includes(String(type))"
        class="echarts-container-box"
    >
      <div ref="chartRef" class="echarts-wrapper"></div>
    </div>

    <!-- HTML -->
    <div
        v-if="String(type) === '6'"
        v-html="chartData"
        class="html-content"
    ></div>

  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import * as echarts from 'echarts'

/* ================= props ================= */

const props = defineProps({
  type: { type: [String, Number], required: true },
  chartData: { type: [Array, Object, String], default: () => [] },
  field: { type: [Array, String], default: () => [] }
})

/* ================= refs ================= */

const chartRef = ref(null)
let myChart = null
let resizeObserver = null
let inited = false   // 👈 关键：只初始化一次

/* ================= computed ================= */

const headers = computed(() => {
  if (!props.field) return []
  if (Array.isArray(props.field)) return props.field
  if (typeof props.field === 'string') {
    return props.field.replace(/[{}]/g, '').split(',').map(s => s.trim())
  }
  return []
})

/* ================= helpers ================= */

const formatLongText = (v) =>
    typeof v === 'string' && v.length > 8 ? v.slice(0, 7) + '…' : v

const getOption = () => {
  if (!props.chartData || Object.keys(props.chartData).length === 0) return null

  const xAxisData = Object.keys(props.chartData)
  const yAxisData = Object.values(props.chartData)

  const base = {
    backgroundColor: 'transparent',
    grid: { top: '15%', left: '10%', right: '10%', bottom: '25%', containLabel: true },
    tooltip: {
      trigger: 'axis',
      confine: true,
      backgroundColor: 'rgba(10,10,31,.9)',
      textStyle: { color: '#fff' }
    },
    legend: {
      bottom: 0,
      textStyle: { color: '#A0A0BF', fontSize: 11 }
    }
  }

  if (String(props.type) === '2') {
    return {
      ...base,
      xAxis: {
        type: 'category',
        data: xAxisData,
        axisLabel: { rotate: 30, formatter: formatLongText }
      },
      yAxis: { type: 'value' },
      series: [{
        type: 'line',
        smooth: true,
        data: yAxisData,
        areaStyle: {}
      }]
    }
  }

  if (String(props.type) === '3') {
    return {
      ...base,
      xAxis: {
        type: 'category',
        data: xAxisData,
        axisLabel: { formatter: formatLongText }
      },
      yAxis: { type: 'value' },
      series: [{
        type: 'bar',
        data: yAxisData,
        barWidth: '40%',
        label: { show: true, position: 'top' }
      }]
    }
  }

  if (String(props.type) === '4') {
    return {
      ...base,
      tooltip: { trigger: 'item' },
      legend: { orient: 'vertical', right: '5%', top: 'center' },
      series: [{
        type: 'pie',
        radius: ['45%', '70%'],
        center: ['40%', '50%'],
        data: xAxisData.map((k, i) => ({
          name: k,
          value: yAxisData[i]
        }))
      }]
    }
  }

  return null
}

/* ================= core ================= */

/**
 * 真正稳定的 render
 * - init 只做一次
 * - 不再 RAF 自旋
 * - DOM 不可用就直接 return
 */
const renderChart = async () => {
  if (!['2', '3', '4'].includes(String(props.type))) return
  if (!chartRef.value) return

  await nextTick()

  const el = chartRef.value
  if (el.clientWidth === 0 || el.clientHeight === 0) return

  if (!myChart) {
    myChart = echarts.init(el)
    inited = true
  }

  const option = getOption()
  if (option) {
    myChart.setOption(option, true)
    myChart.resize()
  }
}

/* ================= watchers ================= */

// type 切换
watch(() => props.type, () => {
  if (inited) {
    myChart?.clear()
  }
  renderChart()
})

// 数据流式更新
watch(
    () => props.chartData,
    () => {
      if (inited) {
        const option = getOption()
        option && myChart.setOption(option, true)
      } else {
        renderChart()
      }
    },
    { deep: true }
)

/* ================= lifecycle ================= */

onMounted(async () => {
  await nextTick()

  resizeObserver = new ResizeObserver(() => {
    if (!chartRef.value) return
    if (chartRef.value.clientWidth > 0) {
      myChart?.resize()
    }
  })

  resizeObserver.observe(chartRef.value)

  // 等对话气泡布局完成（关键）
  requestAnimationFrame(() => {
    requestAnimationFrame(renderChart)
  })
})

onUnmounted(() => {
  resizeObserver?.disconnect()
  myChart?.dispose()
  myChart = null
  inited = false
})
</script>

<style scoped>
.chart-container {
  width: 100%;
  margin: 15px 0;
}

/* ===== table ===== */

.table-wrapper {
  background: rgba(255,255,255,.03);
  border-radius: 12px;
  border: 1px solid rgba(0,123,255,.25);
  overflow: auto;
  max-height: 400px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  color: #E2E8F0;
}

.data-table th {
  position: sticky;
  top: 0;
  background: rgba(0,123,255,.15);
  color: #00D1FF;
  padding: 12px;
}

.data-table td {
  padding: 10px;
  border-bottom: 1px solid rgba(255,255,255,.05);
}

/* ===== echarts ===== */

.echarts-container-box {
  width: 100%;
  min-width: 800px;
  height: 400px;
  padding: 15px;
  border-radius: 12px;
  background: rgba(0,0,0,.2);
}

.echarts-wrapper {
  width: 100%;
  height: 100%;
}
</style>
